import { Box, SxProps, Theme } from '@mui/material'
import { PowerLeftIcon, PowerRightIcon, useMinimalsTheme } from '@tom-ui/ui'

type ReeferRailVariant = 'filled' | 'outlined' | 'ghost'

interface ReeferRailCellProps {
  showLeftIcon?: boolean
  showRightIcon?: boolean
  /** 'filled' (default): grey background, optional white icons (details grid rail).
   *  'outlined': white background with coloured icons (setup view). Supports `highlighted`.
   *  'ghost': no background, larger grey icons (details grid footer). */
  variant?: ReeferRailVariant
  /** When true, uses primary colour as background and white icons. Only applies to 'outlined' variant. */
  highlighted?: boolean
  /** Additional sx overrides (grid placement, sizing, border radius, etc.). */
  sx?: SxProps<Theme>
}

export const ReeferRailCell = ({
  showLeftIcon,
  showRightIcon,
  variant = 'filled',
  highlighted,
  sx,
}: ReeferRailCellProps) => {
  const theme = useMinimalsTheme()

  const isFilled = variant === 'filled'
  const isOutlined = variant === 'outlined'
  const isGhost = variant === 'ghost'
  const isHighlighted = isOutlined && highlighted

  const backgroundColor = isFilled
    ? theme.palette.grey[300]
    : isHighlighted
      ? theme.palette.primary.main
      : isGhost
        ? undefined
        : 'white'

  const iconColor = isFilled || isHighlighted
    ? theme.palette.common.white
    : isGhost
      ? theme.palette.grey[500]
      : theme.palette.secondary.main

  const iconSize = isGhost ? 20 : 12

  return (
    <Box
      sx={{
        border: isFilled ? `1px solid ${theme.palette.grey[300]}` : undefined,
        borderRadius: isFilled ? theme.customRadius.small : undefined,
        backgroundColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.customSpacing.xxxs,
        ...sx,
      }}
    >
      {showLeftIcon && <PowerLeftIcon sx={{ fontSize: iconSize, color: iconColor }} />}
      {showRightIcon && <PowerRightIcon sx={{ fontSize: iconSize, color: iconColor }} />}
    </Box>
  )
}
